// Core: every page registers on its first render.

import { publish } from "../shared/publish";

// Unsaved-data guard of DwsPageBaseComponent. Every page registers itself with its own dirty flag, and the browser asks
// "Leave site?" when ANY registered page has unsaved data. It used to be one global flag and one owner: the last page to
// register replaced the handler and reset the flag, so an inline page nested in a host page (or the next page of an
// in-app navigation registering before the previous one was disposed) silently cleared the host's unsaved state.
// Pages are keyed by their DotNetObjectReference id: Blazor revives a new wrapper object for every call.
type DwsPageReference = DotNetObject & { _id?: number };

const dwsUnloadPages = new Map<unknown, { helper: DwsPageReference; dirty: boolean }>();
let beforeUnloadHandler: ((e: BeforeUnloadEvent) => string | undefined) | null = null;

function dwsUnloadKey(dotNetHelper: DwsPageReference): unknown {
    return dotNetHelper && dotNetHelper._id !== undefined ? dotNetHelper._id : dotNetHelper;
}

function dwsAnyPageHasUnsavedData(): boolean {
    for (const page of dwsUnloadPages.values()) {
        if (page.dirty) {
            return true;
        }
    }

    return false;
}

// Kept for code that reads it: true while any registered page has unsaved data.
Object.defineProperty(window, 'dwsHasUnsavedData', { get: dwsAnyPageHasUnsavedData, configurable: true });

function setHasUnsavedData(hasUnsaved: boolean, dotNetHelper?: DwsPageReference): void {
    let page = dotNetHelper ? dwsUnloadPages.get(dwsUnloadKey(dotNetHelper)) : null;

    // A caller without a page reference (older code) means the most recently registered page.
    if (!page && !dotNetHelper && dwsUnloadPages.size > 0) {
        page = Array.from(dwsUnloadPages.values()).pop();
    }

    if (page) {
        page.dirty = !!hasUnsaved;
    }
}

function registerBeforeUnload(dotNetHelper: DwsPageReference): void {
    dwsUnloadPages.set(dwsUnloadKey(dotNetHelper), { helper: dotNetHelper, dirty: false });

    if (beforeUnloadHandler) {
        return;
    }

    beforeUnloadHandler = function (e: BeforeUnloadEvent): string | undefined {
        if (dwsAnyPageHasUnsavedData()) {
            e.preventDefault();
            e.returnValue = true;
            return '';
        }

        for (const page of dwsUnloadPages.values()) {
            const result = page.helper.invokeMethodAsync("OnBrowserUnload");
            if (result && typeof result.catch === 'function') {
                result.catch(() => { });
            }
        }
    };

    window.addEventListener("beforeunload", beforeUnloadHandler);
}

function unregisterBeforeUnload(dotNetHelper?: DwsPageReference): void {
    if (dotNetHelper) {
        dwsUnloadPages.delete(dwsUnloadKey(dotNetHelper));
    } else {
        dwsUnloadPages.clear();
    }

    if (dwsUnloadPages.size === 0 && beforeUnloadHandler) {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
        beforeUnloadHandler = null;
    }
}

publish("setHasUnsavedData", setHasUnsavedData);
publish("registerBeforeUnload", registerBeforeUnload);
publish("unregisterBeforeUnload", unregisterBeforeUnload);
